import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, MapPin, Smartphone, Star, StarHalf, Users } from 'lucide-react';
import { publicApi, apiAbsoluteUrl } from '../lib/api';
import { BrandMark } from '../components/BrandMark';

type VenueCard = {
  name: string;
  address: string;
  rating: number | null;
  ratings_total: number | null;
  photo_url: string | null;
};

/** דירוג כוכבים בסגנון Google */
function StarsRow({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`דירוג ${rating}`}>
      {[1, 2, 3, 4, 5].map((i) =>
        rating >= i - 0.25 ? (
          <Star key={i} size={13} className="fill-[#E8A93C] text-[#E8A93C]" aria-hidden />
        ) : rating >= i - 0.75 ? (
          <StarHalf key={i} size={13} className="fill-[#E8A93C] text-[#E8A93C]" aria-hidden />
        ) : (
          <Star key={i} size={13} className="text-line-strong" aria-hidden />
        ),
      )}
    </span>
  );
}

type InviteEvent = {
  guest_first_name: string | null;
  guest_name: string;
  status: string;
  attending_count: number | null;
  name: string;
  event_date: string | null;
  event_time: string | null;
  venue_name: string | null;
  venue_address: string | null;
  venue_lat?: number | null;
  venue_lng?: number | null;
  invitation_image_url: string | null;
  max_plus_ones: number;
  ride_token: string | null;
};

type Status = 'confirmed' | 'declined' | 'maybe';

function formatDate(date: string | null, time: string | null): string {
  if (!date) return '';
  try {
    const d = new Date(date);
    const ds = new Intl.DateTimeFormat('he-IL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long',
    }).format(d);
    const t = time ? time.slice(0, 5) : '';
    return t ? `${ds} · ${t}` : ds;
  } catch {
    return date;
  }
}

/** לוגו Waze הרשמי (simple-icons) */
function WazeIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#33CCFF" aria-hidden>
      <path d="M13.218 0C9.915 0 6.835 1.49 4.723 4.148c-1.515 1.913-2.31 4.272-2.31 6.706v1.739c0 .894-.62 1.738-1.862 1.813-.298.025-.547.224-.547.522-.05.82.82 2.31 2.012 3.502.82.844 1.788 1.515 2.832 2.036a3 3 0 0 0 2.955 3.528 2.966 2.966 0 0 0 2.931-2.385h2.509c.323 1.689 2.086 2.856 3.974 2.21 1.64-.546 2.36-2.409 1.763-3.924a12.84 12.84 0 0 0 1.838-1.465 10.73 10.73 0 0 0 3.18-7.65c0-2.882-1.118-5.589-3.155-7.625A10.899 10.899 0 0 0 13.218 0zm0 1.217c2.558 0 4.967.994 6.78 2.807a9.525 9.525 0 0 1 2.807 6.78A9.526 9.526 0 0 1 20 17.585a9.647 9.647 0 0 1-6.78 2.807h-2.46a3.008 3.008 0 0 0-2.93-2.41 3.03 3.03 0 0 0-2.534 1.367v.024a8.945 8.945 0 0 1-2.41-1.788c-.844-.844-1.316-1.614-1.515-2.11a2.858 2.858 0 0 0 1.441-.846 2.959 2.959 0 0 0 .795-2.036v-1.789c0-2.11.696-4.197 2.012-5.861 1.863-2.385 4.62-3.726 7.6-3.726zm-2.41 5.986a1.192 1.192 0 0 0-1.191 1.192 1.192 1.192 0 0 0 1.192 1.193A1.192 1.192 0 0 0 12 8.395a1.192 1.192 0 0 0-1.192-1.192zm7.204 0a1.192 1.192 0 0 0-1.192 1.192 1.192 1.192 0 0 0 1.192 1.193 1.192 1.192 0 0 0 1.192-1.193 1.192 1.192 0 0 0-1.192-1.192zm-7.377 4.769a.596.596 0 0 0-.546.845 4.813 4.813 0 0 0 4.346 2.757 4.77 4.77 0 0 0 4.347-2.757.596.596 0 0 0-.547-.845h-.025a.561.561 0 0 0-.521.348 3.59 3.59 0 0 1-3.254 2.061 3.591 3.591 0 0 1-3.254-2.061.64.64 0 0 0-.546-.348z" />
    </svg>
  );
}

/** לוגו Google Maps הרשמי (simple-icons) */
function GoogleMapsIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#4285F4" aria-hidden>
      <path d="M19.527 4.799c1.212 2.608.937 5.678-.405 8.173-1.101 2.047-2.744 3.74-4.098 5.614-.619.858-1.244 1.75-1.669 2.727-.141.325-.263.658-.383.992-.121.333-.224.673-.34 1.008-.109.314-.236.684-.627.687h-.007c-.466-.001-.579-.53-.695-.887-.284-.874-.581-1.713-1.019-2.525-.51-.944-1.145-1.817-1.79-2.671L19.527 4.799zM8.545 7.705l-3.959 4.707c.724 1.54 1.821 2.863 2.871 4.18.247.31.494.622.737.936l4.984-5.925-.029.01c-1.741.601-3.691-.291-4.392-1.987a3.377 3.377 0 0 1-.209-.716c-.063-.437-.077-.761-.004-1.198l.001-.007zM5.492 3.149l-.003.004c-1.947 2.466-2.281 5.88-1.117 8.77l4.785-5.689-.058-.05-3.607-3.035zM14.661.436l-3.838 4.563a.295.295 0 0 1 .027-.01c1.6-.551 3.403.15 4.22 1.626.176.319.323.683.377 1.045.068.446.085.773.012 1.22l-.003.016 3.836-4.561A8.382 8.382 0 0 0 14.67.439l-.009-.003zM9.466 5.868L14.162.285l-.047-.012A8.31 8.31 0 0 0 11.986 0a8.439 8.439 0 0 0-6.169 2.766l-.016.018 3.665 3.084z" />
    </svg>
  );
}

function navLinks(event: InviteEvent): { waze: string | null; maps: string | null } {
  const lat = event.venue_lat != null ? Number(event.venue_lat) : null;
  const lng = event.venue_lng != null ? Number(event.venue_lng) : null;
  const q = [event.venue_name, event.venue_address].filter(Boolean).join(', ').trim();
  if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    return {
      waze: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
      maps: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
    };
  }
  if (!q) return { waze: null, maps: null };
  return {
    waze: `https://waze.com/ul?q=${encodeURIComponent(q)}`,
    maps: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`,
  };
}

export default function PublicInvitePage() {
  const { token = '' } = useParams();
  const [event, setEvent] = useState<InviteEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState(0);
  const [status, setStatus] = useState<Status | null>(null);
  const [count, setCount] = useState('1');
  const [venueCard, setVenueCard] = useState<VenueCard | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{
    message: string;
    status: Status;
    ride_url: string | null;
  } | null>(null);

  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/invite/${token}`)
      .then((r) => {
        if (!alive) return;
        setEvent(r.data);
        if (r.data.attending_count) setCount(String(r.data.attending_count));
      })
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  // כרטיס העסק של האולם מ-Google (תמונה + דירוג)
  const cardVenueName = event?.venue_name || '';
  const cardVenueAddress = event?.venue_address || '';
  useEffect(() => {
    if (!cardVenueName && !cardVenueAddress) return;
    let alive = true;
    publicApi
      .get('/public/places/card', { params: { name: cardVenueName, address: cardVenueAddress } })
      .then((r) => alive && setVenueCard(r.data?.card ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [cardVenueName, cardVenueAddress]);

  const maxCount = 20;
  const hi = event?.guest_first_name || event?.guest_name || '';

  async function submitFinal(nextStatus: Status, attending?: number) {
    setSubmitting(true);
    setError('');
    try {
      const r = await publicApi.post(`/public/invite/${token}`, {
        status: nextStatus,
        attending_count: nextStatus === 'confirmed' ? attending : undefined,
      });
      setDone({
        message: r.data.message,
        status: r.data.status,
        ride_url: r.data.ride_url,
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'שגיאה בשליחה');
    } finally {
      setSubmitting(false);
    }
  }

  function onPickStatus(s: Status) {
    setStatus(s);
    setError('');
    if (s === 'confirmed') {
      setStep(1);
      return;
    }
    void submitFinal(s);
  }

  function onSubmitCount(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(count);
    if (!Number.isFinite(n) || n < 1 || n > maxCount) {
      setError(`בחרו מספר בין 1 ל-${maxCount}`);
      return;
    }
    void submitFinal('confirmed', n);
  }

  if (loading) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6" role="status" aria-live="polite">
        <div className="text-center space-y-3">
          <div className="mx-auto h-10 w-10 rounded-full border-2 border-sage-200 border-t-sage-600 animate-spin" />
          <p className="text-ink-mute text-sm">טוען הזמנה…</p>
        </div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6">
        <div className="text-center max-w-sm surface p-8">
          <h1 className="font-display text-2xl font-bold text-ink">ההזמנה לא נמצאה</h1>
          <p className="text-ink-mute mt-2 text-sm">ייתכן שהקישור שגוי או שפג תוקף.</p>
        </div>
      </div>
    );
  }

  if (done) {
    const appDeepLink = `dowe://guest/${token}`;
    return (
      <div dir="rtl" className="min-h-full flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md surface p-8 text-center space-y-4 page-enter" role="status">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-sage-100 text-sage-700 grid place-items-center">
            <CheckCircle2 size={28} aria-hidden />
          </div>
          <h1 className="font-display text-3xl font-bold text-ink">תודה{hi ? ` ${hi}` : ''}!</h1>
          <p className="text-ink-mute text-[15px] leading-relaxed">{done.message}</p>

          {done.status === 'confirmed' && (
            <div className="rounded-2xl border border-sage-200 bg-sage-50/60 p-5 space-y-3 text-right">
              <div className="flex items-center gap-2 justify-center">
                <Smartphone size={18} className="text-sage-700" aria-hidden />
                <h2 className="font-semibold text-ink text-[16px]">הצטרפו לאפליקציית האירוע</h2>
              </div>
              <p className="text-[13.5px] text-ink-mute leading-relaxed text-center">
                סטוריז מהחתונה, גלריה משותפת להורדה וטרמפים — הכול באפליקציית DoWe.
              </p>
              <a href={appDeepLink} className="btn-primary w-full inline-flex justify-center">
                כניסה לאפליקציה
              </a>
              <div className="flex flex-col items-center gap-2 pt-1">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(appDeepLink)}`}
                  alt="קוד QR לכניסה לאפליקציית האירוע"
                  width={130}
                  height={130}
                  className="rounded-xl border border-line bg-white p-2"
                  loading="lazy"
                />
                <p className="text-[12px] text-ink-faint">סרקו מהטלפון אם האפליקציה מותקנת</p>
              </div>
            </div>
          )}

          {done.status === 'confirmed' && event.ride_token && (
            <Link to={`/r/${event.ride_token}`} className="btn-secondary w-full mt-2 inline-flex justify-center">
              רישום לטרמפ (אופציונלי)
            </Link>
          )}
        </div>
      </div>
    );
  }

  const nav = navLinks(event);

  return (
    <div dir="rtl" className="min-h-full">
      <a href="#rsvp" className="skip-link">
        דלג לאישור הגעה
      </a>

      {event.invitation_image_url && (
        <div className="w-full max-h-[40vh] overflow-hidden bg-sage-100">
          <img
            src={event.invitation_image_url}
            alt={`הזמנה ל${event.name}`}
            className="w-full h-full object-cover max-h-[40vh]"
          />
        </div>
      )}

      <div className="max-w-md mx-auto px-4 py-8 page-enter">
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-sage-600 mb-2">הזמנה</p>
        <h1 className="font-display text-4xl font-bold text-ink tracking-tight leading-tight">{event.name}</h1>
        {hi && <p className="text-ink-mute mt-2 text-[15px]">שלום {hi},</p>}

        <div className="mt-5 space-y-3 text-[14px] text-ink-mute">
          {event.event_date && (
            <p className="inline-flex items-center gap-2">
              <CalendarDays size={16} className="text-sage-600 shrink-0" aria-hidden />
              <span>{formatDate(event.event_date, event.event_time)}</span>
            </p>
          )}
          {(event.venue_name || event.venue_address) && (
            <div className="space-y-2">
              {venueCard ? (
                <div className="rounded-2xl overflow-hidden border border-line bg-paper-elev shadow-sm">
                  {venueCard.photo_url && (
                    <img
                      src={apiAbsoluteUrl(venueCard.photo_url)}
                      alt={venueCard.name}
                      className="w-full h-44 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-4 space-y-1.5">
                    <p className="font-display font-bold text-ink text-[17px]">{venueCard.name}</p>
                    {venueCard.rating != null && (
                      <p className="flex items-center gap-1.5 text-[13px] text-ink-soft">
                        <span className="font-bold">{venueCard.rating.toFixed(1)}</span>
                        <StarsRow rating={venueCard.rating} />
                        {venueCard.ratings_total != null && (
                          <span className="text-ink-faint">
                            ({venueCard.ratings_total.toLocaleString('he-IL')} ביקורות ב-Google)
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-[13px] text-ink-mute inline-flex items-center gap-1.5">
                      <MapPin size={13} className="text-sage-600 shrink-0" aria-hidden />
                      {venueCard.address || event.venue_address}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="inline-flex items-start gap-2">
                  <MapPin size={16} className="text-sage-600 shrink-0 mt-0.5" aria-hidden />
                  <span>
                    {event.venue_name}
                    {event.venue_name && event.venue_address ? (
                      <>
                        <br />
                        <span className="text-ink-faint">{event.venue_address}</span>
                      </>
                    ) : (
                      event.venue_address
                    )}
                  </span>
                </p>
              )}
              {(nav.waze || nav.maps) && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {nav.waze && (
                    <a
                      href={nav.waze}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-secondary !text-[13px] inline-flex items-center gap-1.5"
                    >
                      <WazeIcon />
                      ניווט ב-Waze
                    </a>
                  )}
                  {nav.maps && (
                    <a
                      href={nav.maps}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-secondary !text-[13px] inline-flex items-center gap-1.5"
                    >
                      <GoogleMapsIcon />
                      Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div id="rsvp" className="surface p-5 sm:p-6 mt-8 space-y-4" aria-live="polite">
          {step === 0 && (
            <>
              <h2 className="font-semibold text-ink text-[17px] text-center">האם אתם מגיעים לחתונה?</h2>
              <div className="grid gap-2.5" role="group" aria-label="בחירת הגעה">
                {(
                  [
                    { id: 'confirmed' as const, label: 'כן, מגיעים' },
                    { id: 'declined' as const, label: 'לא נוכל להגיע' },
                    { id: 'maybe' as const, label: 'עדיין לא בטוחים' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={submitting}
                    onClick={() => onPickStatus(opt.id)}
                    aria-pressed={status === opt.id}
                    className={`min-h-touch py-3.5 px-4 rounded-xl border text-[15px] font-semibold transition-all cursor-pointer focus-visible:shadow-focus focus-visible:outline-none ${
                      status === opt.id
                        ? 'border-sage-500 bg-sage-50 text-sage-800 shadow-lift'
                        : 'border-line bg-white text-ink hover:border-sage-300 hover:bg-sage-50/50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <form onSubmit={onSubmitCount} className="space-y-4">
              <button
                type="button"
                className="text-[13px] font-medium text-ink-mute hover:text-sage-700 cursor-pointer min-h-touch inline-flex items-center"
                onClick={() => {
                  setStep(0);
                  setStatus(null);
                }}
              >
                ← חזרה
              </button>
              <h2 className="font-semibold text-ink text-[17px] flex items-center gap-2 justify-center">
                <Users size={18} className="text-sage-600" aria-hidden />
                כמה תהיו?
              </h2>
              <p className="text-[13px] text-ink-mute text-center">כולל אתכם (עד {maxCount})</p>
              <label htmlFor="guest-count" className="sr-only">
                מספר אורחים
              </label>
              <input
                id="guest-count"
                type="number"
                min={1}
                max={maxCount}
                inputMode="numeric"
                className="field text-center text-lg font-semibold"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                required
                autoFocus
                aria-describedby="guest-count-hint"
              />
              <p id="guest-count-hint" className="sr-only">
                בחרו מספר בין 1 ל-{maxCount}
              </p>
              <button type="submit" disabled={submitting} className="btn-primary w-full">
                {submitting ? 'שולח…' : 'אישור'}
              </button>
            </form>
          )}

          {error && (
            <p className="text-[13px] text-red-700 text-center font-medium" role="alert">
              {error}
            </p>
          )}
          {submitting && step === 0 && (
            <p className="text-[13px] text-ink-faint text-center" role="status">
              שומר…
            </p>
          )}
        </div>

        <div className="mt-10 flex justify-center opacity-80">
          <BrandMark size="sm" showTagline />
        </div>
      </div>
    </div>
  );
}
